<script setup lang="ts">
import type { AppFlatSetting, SancareFlatSettings } from '@sancare/typescript-commons/src/settings/types'
import dayjs from 'dayjs'
import _ from 'lodash'
import { computed } from 'vue'

import { McoStaySuggestionItem, McoStaySuggestionPrediction, SuggestionJustification } from '@/misc/types'
import { findMajorRumId } from '@/stay-displayer/composables/useAutomationPreview'
import { CodingSelection, Suggestion, SuggestionDiagnosis, SuggestionSearch } from '@/stay-displayer/types'
import { useStore } from '@/store'
import { CountryEnum, ModeEnum } from '@/store/modules/settings/types'
import { Ghm, StandardModeDiagnosisGroup } from '@/store/modules/stay/mco/types'
import { StayHealthData } from '@/store/modules/stay/types'

import { isRejectedOnRum, splitWildcardsByEvaluation } from './diagnosis-lists/rejectedSuggestions'
import MultiSelectedItemList from './diagnosis-lists/standard-mode/MultiSelectedItemsList.vue'
import SingleSelectedItemList from './diagnosis-lists/standard-mode/SingleSelectedItemList.vue'

const props = withDefaults(defineProps<{
  appSettings: AppFlatSetting
  displayedRumIdx: number
  ghm?: Ghm | null
  healthData: StayHealthData
  isLoading?: boolean
  mode: ModeEnum
  rumId: number
  selection: CodingSelection | null
  sancareSettings: SancareFlatSettings
  standardModeDiagnosisGroup: StandardModeDiagnosisGroup
  standardModeExcludedDiagnoses: string[]
  stayHasInitialCoding: boolean
  // Optional: fetched separately from the main stay payload and merged in once it resolves — the
  // stay is displayed before that merge completes, so this can briefly be absent (see getStaySuccess).
  staySuggestions?: McoStaySuggestionItem[]
  // Already restricted to the displayed RUM's evaluations by the page
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>(), {
  ghm: null,
  selection: null,
  isLoading: false,
  staySuggestions: () => [],
  suggestionJustifications: null,
})

const emit = defineEmits<{
  'make-selection': [selection: CodingSelection]
}>()

const store = useStore()

const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const stayEnd = computed(() => store.state.stayDisplayer.displayedStay.stayEnd)

// The backend aggregates predictions across every RUM of the stay on the suggestion item itself, so
// the currently displayed RUM's own prediction is looked up by its rum scope instead.
function resolvePredictions(predictions: McoStaySuggestionPrediction[]): McoStaySuggestionPrediction[] {
  return predictions.filter((prediction) => prediction.rum.sancareId === props.rumId)
}

// description/level are already resolved server-side from the PMSI nomenclature by reference (see
// ListStaySuggestionsUseCase), independent of any matching search — no client-side override needed
// anymore. A chronic DAS with neither (see buildSuggestion's isChronic exception below) falls back to
// the diagnosis description/level the backend already resolved for it (SuggestionRepository's
// chronic_das CTE) — there is no search/prediction diagnosis to pull it from otherwise.
// canShowRational: a search match always shows its justification; a purely-AI suggestion shows one
// whenever the backend found a justification for it. The justifications fetch itself is not
// threshold-filtered (includeBelowThreshold=1, see prefetchMcoStayJustifications) — it mirrors
// whatever predictions the suggestions list already surfaces, so anything shown as a suggestion can
// also show its rationale, regardless of confidence.
function buildSuggestionDiagnosis(real: { reference: string, description: string | null, level: number | null, isChronic: boolean, chronicDescription: string | null, chronicLevel: number | null }, type: string, matchedSearches: SuggestionSearch[], matchedPredictions: McoStaySuggestionPrediction[]): SuggestionDiagnosis {
  const prediction = matchedPredictions[0] ?? null

  const justification = props.suggestionJustifications?.[`${type}:${real.reference}`]
  // A suggestion can carry only evaluations, with no textual hint at all.
  const canShowRational = matchedSearches.length
    ? true
    : Boolean(justification?.textualJustifications.length || justification?.evaluations.length)

  return {
    reference: real.reference,
    level: real.level ?? real.chronicLevel ?? undefined,
    description: real.description ?? real.chronicDescription,
    isChronic: real.isChronic,
    excluded: props.standardModeExcludedDiagnoses.indexOf(real.reference) !== -1,
    canShowRational,
    staySavedSearches: matchedSearches,
    prediction,
  }
}

// Builds a Suggestion from a backend suggestion item: existence, wildcard grouping and search
// associations come from the backend; predictions are hydrated from the currently displayed RUM's
// own predicted labels (see resolvePredictions) so a suggestion is dropped when nothing in it is
// relevant to that RUM (e.g. it only exists because another RUM of the stay has a prediction for it).
// A chronic DAS is the one exception kept regardless of search/prediction match: the backend already
// suggests it purely because of the patient's history (SuggestionRepository's chronic_das CTE), with
// no round-trip through the old dedicated chronicAssociatedDiagnoses fetch needed anymore.
function buildSuggestion(item: McoStaySuggestionItem, type: string): Suggestion | null {
  // The backend aggregates wildcard-matched searches on the parent item, not on each wildcard
  // child (a real diagnosis matched only via the wildcard pattern has its own `searches` empty —
  // see SuggestionRepository::getSuggestionsMcoForStay's searches_agg, grouped by the wildcard
  // parent reference) — every child is suggested by the parent's searches plus any of its own.
  const realDiagnosisRefs = item.isWildcard
    ? item.wildcardChildren.map((child) => ({ reference: child.reference, searches: [...item.searches, ...child.searches], predictions: child.predictions, description: child.description, level: child.level, isChronic: child.isChronic, chronicDescription: child.chronicDescription, chronicLevel: child.chronicLevel }))
    : [{ reference: item.reference, searches: item.searches, predictions: item.predictions, description: item.description, level: item.level, isChronic: item.isChronic, chronicDescription: item.chronicDescription, chronicLevel: item.chronicLevel }]

  const suggestion: Suggestion = {
    reference: item.reference,
    description: null,
    isWildcard: item.isWildcard,
    level: { min: null, max: null, label: null },
    confidence: { min: null, max: null, label: null },
    diagnoses: [],
    searches: [],
    predictions: [],
    isExcluded: false,
    isInitialDiag: false,
    isCurrentDiag: false,
    isRevaluating: false,
    isOverThreshold: false,
    isLowLevel: false,
    isAlwaysVisible: false,
    hasRejectedEvaluation: isRejectedOnRum(item.rejectedEvaluationRumIds, props.rumId),
  }

  const matchedSearchesById = new Map<number, SuggestionSearch>()

  for (const real of realDiagnosisRefs) {
    const matchedSearches: SuggestionSearch[] = real.searches.map((search) => ({ search: { id: search.sancareId, name: search.name, type: search.type } }))
    const matchedPredictions = resolvePredictions(real.predictions)
    if (!matchedSearches.length && !matchedPredictions.length && !real.isChronic) {
      continue
    }

    for (const ss of matchedSearches) {
      matchedSearchesById.set(ss.search.id, ss)
    }
    suggestion.predictions.push(...matchedPredictions)

    const diagnosis = buildSuggestionDiagnosis(real, type, matchedSearches, matchedPredictions)
    suggestion.diagnoses.push(diagnosis)

    if (diagnosis.level) {
      suggestion.level.min = suggestion.level.min === null ? diagnosis.level : Math.min(suggestion.level.min, diagnosis.level)
      suggestion.level.max = suggestion.level.max === null ? diagnosis.level : Math.max(suggestion.level.max, diagnosis.level)
    }
    for (const prediction of matchedPredictions) {
      suggestion.confidence.min = suggestion.confidence.min === null ? prediction.confidence : Math.min(suggestion.confidence.min, prediction.confidence)
      suggestion.confidence.max = suggestion.confidence.max === null ? prediction.confidence : Math.max(suggestion.confidence.max, prediction.confidence)
    }
  }

  if (!suggestion.diagnoses.length) {
    return null
  }
  suggestion.searches = Array.from(matchedSearchesById.values())

  return suggestion
}

function computeSuggestionFlags(suggestion: Suggestion, suggestedDiagRef: string, type: string, suggestionRegex: string): Pick<Suggestion, 'isInitialDiag' | 'isCurrentDiag' | 'isRevaluating' | 'isExcluded' | 'isLowLevel' | 'isOverThreshold'> {
  let isInitialDiag
  let isCurrentDiag
  let isRevaluating = true
  let isExcluded = false
  let isLowLevel = false
  let isOverThreshold = false
  if (type === 'DP' || type === 'DR') {
    isInitialDiag = props.standardModeDiagnosisGroup[`first${type}`] && props.standardModeDiagnosisGroup[`first${type}`].reference.match(suggestionRegex) !== null
    isCurrentDiag = props.standardModeDiagnosisGroup[`current${type}`] && props.standardModeDiagnosisGroup[`current${type}`].reference.match(suggestionRegex) !== null
    if (suggestion.confidence.max) {
      isOverThreshold = suggestion.confidence.max >= props.appSettings[(type === 'DP' ? 'standard_mode_dp_threshold' : 'standard_mode_dr_threshold')]
    }
  } else {
    isInitialDiag = props.standardModeDiagnosisGroup.associatedDiags.some(
      (associatedDiag) => (
        associatedDiag.associatedDiagnosis.reference === suggestedDiagRef
        && ['external', 'unsure', 'prediction_unsure'].includes(associatedDiag.codingOrigin)
        && !associatedDiag.disabled
      )
    )
    isCurrentDiag = props.standardModeDiagnosisGroup.associatedDiags.some(
      (associatedDiag) => (
        associatedDiag.associatedDiagnosis.reference === suggestedDiagRef
        && !associatedDiag.disabled
      )
    )
    isRevaluating = (
      props.ghm && !_.isNaN(parseInt(props.ghm.level)) && suggestion.level.max > parseInt(props.ghm.level)
    )
    isExcluded = suggestion.diagnoses.every((d) => d.excluded)
    isLowLevel = suggestion.level.max === 1
    isOverThreshold = suggestion.confidence.max !== null && suggestion.confidence.max >= props.appSettings[props.stayHasInitialCoding ? 'standard_mode_das_threshold' : 'new_coding_das_threshold']
  }

  return {
    isInitialDiag,
    isCurrentDiag,
    isRevaluating,
    isExcluded,
    isLowLevel,
    isOverThreshold,
  }
}

// Finalizes a built Suggestion: labels, then the visibility flags (unchanged logic) — kept as a
// separate step so buildSuggestion above stays focused on existence/matching.
function finalizeSuggestion(suggestion: Suggestion, type: string): Suggestion {
  if (suggestion.isWildcard && suggestion.diagnoses.length > 1) {
    suggestion.description = `Diagnostic commençant par ${suggestion.reference.replace('*', '')}`
  } else if (suggestion.diagnoses.length === 1) {
    suggestion.description = _.first(suggestion.diagnoses).description
  }

  if (suggestion.level.min || suggestion.level.max) {
    if (suggestion.level.min === suggestion.level.max) {
      suggestion.level.label = `${suggestion.level.min}`
    } else {
      suggestion.level.label = `${suggestion.level.min} - ${suggestion.level.max}`
    }
  }

  if (suggestion.confidence.min || suggestion.confidence.max) {
    if (suggestion.confidence.min === suggestion.confidence.max) {
      suggestion.confidence.label = `${Math.round(suggestion.confidence.min * 100)}%`
    } else {
      suggestion.confidence.label = `${Math.round(suggestion.confidence.min * 100)}% - ${Math.round(suggestion.confidence.max * 100)}%`
    }
  }

  // Only used to preserve the DP/DR "is this the currently coded diagnosis" regex check below —
  // the diagnosis/search/prediction matching itself no longer relies on this pattern.
  let suggestionRegex = `^${suggestion.reference.replace(/\+/g, '\\+')}$`
  if (suggestion.reference.indexOf('*') === suggestion.reference.length - 1) {
    suggestionRegex = `^${suggestion.reference.slice(0, -1).replace(/\+/g, '\\+')}`
  }

  return {
    ...suggestion,
    ...computeSuggestionFlags(suggestion, suggestion.reference, type, suggestionRegex),
  }
}

function buildSuggestions(type: string): Suggestion[] {
  const showLowLevel = props.appSettings[!props.stayHasInitialCoding ? 'new_coding_stays_show_low_das' : 'optimizable_stays_show_low_das']
  const showExcluded = props.appSettings[!props.stayHasInitialCoding ? 'new_coding_stays_show_excluded_das' : 'optimizable_stays_show_excluded_das']
  const suggestions = splitWildcardsByEvaluation(props.staySuggestions.filter((item) => item.type === type), props.rumId)
    .map((item) => buildSuggestion(item, type))
    .filter((suggestion): suggestion is Suggestion => suggestion !== null)
    .map((suggestion) => finalizeSuggestion(suggestion, type))
    .filter((suggestion) => {
      if (type === 'DAS') {
        if (
          (suggestion.isCurrentDiag && !props.appSettings['optimizable_stays_show_coded_das_found_by_ai'])
          || (suggestion.isLowLevel && !showLowLevel)
          || (suggestion.isExcluded && !showExcluded)
        ) {
          return false
        }
      }
      return true
    })

  const betterSuggestedDiag = _.head(_.orderBy(suggestions, ['isInitialDiag', 'searches.length', 'level.max', 'confidence.max'], ['desc', 'desc', 'desc', 'desc']))
  return suggestions.map((suggestion) => {
    if (suggestion.predictions.length) {
      if (suggestion.isCurrentDiag || suggestion.isOverThreshold) {
        if (props.mode === ModeEnum.QUALITY_STAYS || props.mode === ModeEnum.DIALYSIS) {
          // Quality control audits every stay exhaustively, with no preselection — showing only the
          // single best AI suggestion (as in new-coding) would defeat that purpose. Dialysis mode
          // behaves identically to quality-stays for now (BUILD-780).
          suggestion.isAlwaysVisible = !suggestion.isExcluded
        } else if (props.stayHasInitialCoding) {
          suggestion.isAlwaysVisible = !suggestion.isExcluded && suggestion.isRevaluating
        } else {
          // Compared as objects: both parts of a split wildcard share its reference
          suggestion.isAlwaysVisible = betterSuggestedDiag === suggestion
        }
      }
    }
    if (!suggestion.isAlwaysVisible && suggestion.searches.length) {
      suggestion.isAlwaysVisible = !suggestion.isCurrentDiag && !suggestion.isExcluded
    }
    // A chronic DAS is suggested purely from the patient's history, with no search/prediction of its
    // own to gate visibility on — mirrors the old suggestedChronicDas list, which was always shown.
    if (!suggestion.isAlwaysVisible && suggestion.diagnoses.some((diagnosis) => diagnosis.isChronic)) {
      suggestion.isAlwaysVisible = !suggestion.isCurrentDiag && !suggestion.isExcluded
    }
    return suggestion
  })
}

const DPSuggestions = computed(() => buildSuggestions('DP'))
const DRSuggestions = computed(() => buildSuggestions('DR'))
const DASSuggestions = computed(() => buildSuggestions('DAS'))

const isObsoleteChRelatedDiag = computed(() =>
  pmsiCountry.value === CountryEnum.CH && stayEnd.value >= dayjs('2024-01-01')
)

// The AI cross-check for showRelatedDiags below ("would automation predict a DP needing a DR")
// used to read the precomputed rum.automationModeDiagnosisGroup. That field is gone (BUILD-737);
// this reconstructs just the Z-code signal from the suggestions endpoint's raw prediction
// confidence instead — the endpoint always fetches every prediction (includeBelowThreshold=1) and
// isn't gated by mode, so no extra fetch/threshold-completeness gap is introduced.
function getBestPredictedDiagnosisReference(type: string, rumId: number | null): string | null {
  if (rumId === null) {
    return null
  }
  let best: string | null = null
  let bestConfidence = -1
  for (const item of props.staySuggestions) {
    if (item.type !== type) {
      continue
    }
    const candidates = item.isWildcard ? item.wildcardChildren : [item]
    for (const candidate of candidates) {
      for (const prediction of candidate.predictions) {
        if (prediction.rum.sancareId === rumId && prediction.confidence !== null && prediction.confidence > bestConfidence) {
          bestConfidence = prediction.confidence
          best = candidate.reference
        }
      }
    }
  }
  return best
}

// Vue RSS (agrégat, rumId 0) : comme l'ancien rssRumBuilder, le DP courant vient du RUM majeur
// de l'automatisation IA (isMajorRum du preview, cf. StayDisplayerPage.vue), pas d'un RUM d'id 0
// (qui n'a pas de prédictions propres).
const automationCheckRumId = computed(() => {
  if (props.rumId !== 0) {
    return props.rumId
  }
  return findMajorRumId(store.state.stayDisplayer.aiAutomationPreview)
})

const showRelatedDiags = computed(() =>
  pmsiCountry.value === CountryEnum.CH
  || props.standardModeDiagnosisGroup.currentDR
  || (props.standardModeDiagnosisGroup.currentDP && props.standardModeDiagnosisGroup.currentDP.reference[0] === 'Z')
  || getBestPredictedDiagnosisReference('DP', automationCheckRumId.value)?.[0] === 'Z'
)
</script>

<template>
  <div class="card-container">
    <single-selected-item-list
      :app-settings="appSettings"
      :rum-id="rumId"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      :suggested-diagnoses="DPSuggestions"
      :is-loading="isLoading"
      :selection="selection"
      :health-data="healthData"
      :suggestion-justifications="suggestionJustifications"
      :mode="mode"
      type="DP"
      class="principal-diagnosis-group"
      @make-selection="(selection) => emit('make-selection', selection)"
    />
    <single-selected-item-list
      v-if="showRelatedDiags && !isObsoleteChRelatedDiag"
      :app-settings="appSettings"
      :rum-id="rumId"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      :suggested-diagnoses="DRSuggestions"
      :is-loading="isLoading"
      :selection="selection"
      :health-data="healthData"
      :suggestion-justifications="suggestionJustifications"
      :mode="mode"
      type="DR"
      class="related-diagnosis-group"
      @make-selection="(selection) => emit('make-selection', selection)"
    />
    <multi-selected-item-list
      :app-settings="appSettings"
      :sancare-settings="sancareSettings"
      :ghm="ghm"
      :rum-id="rumId"
      :displayed-rum-idx="displayedRumIdx"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      :suggested-diagnoses="DASSuggestions"
      :selection="selection"
      :is-loading="isLoading"
      :health-data="healthData"
      :suggestion-justifications="suggestionJustifications"
      :standard-mode-excluded-diagnoses="standardModeExcludedDiagnoses"
      :mode="mode"
      class="associated-diagnosis-group"
      type="DAS"
      @make-selection="(suggestion) => emit('make-selection', suggestion)"
    />
  </div>
</template>
